import type {EnterDialogModeFunction} from '../../conversation-types'
import type {DiffAnchor} from '@github-ui/diffs/types'
import type React from 'react'
import type {ComponentPropsWithoutRef} from 'react'
import {useCallback, useImperativeHandle, useRef} from 'react'
import {copyCode} from '../../helpers/copy-code'
import {anchorLinkForSelection} from '../../helpers/line-helpers'
import type {ClientDiffLine, DiffLineWithSelectionInfo} from '../../types'
import {copyText} from '@github-ui/copy-to-clipboard'
import {
  useDialogModeActions,
  useDiffLinesActions,
  useGetDiffLinesInfo,
  useGetFirstLineSelectionOnDrag,
  useGetSelectedDiffLines,
  useGetSelectedLineRange,
} from '../../stores/selectors'
import {getGlobalINPObserver} from '@github-ui/web-vitals'
import {useAnalytics} from '@github-ui/use-analytics'

type CellProps = Omit<ComponentPropsWithoutRef<'td'>, 'onKeyDown' | 'onCompositionStart' | 'onCompositionEnd'> & {
  cellId?: string
  commentDialogOpen?: boolean
  columnIndex: number
  diffAnchor: DiffAnchor
  diffLine: ClientDiffLine
  enterDialogMode?: EnterDialogModeFunction
  lineAnchor?: string
  hasThreads?: boolean
  handleDiffSideCellSelectionBlocking?: (event: React.MouseEvent) => void
  handleExitDialogMode?: () => void
  handleHideMarkersFromFocus?: (state: boolean) => void
  handleDiffCellMouseDown?: (e: React.MouseEvent) => void
  handleStartConversation?: () => void
  handleUserClosedMarkersDialog?: () => void
  isLeftSide: boolean
  isRowSelected?: boolean | undefined
  isSplit: boolean
  ref?: React.Ref<HTMLTableCellElement>
}

export function Cell({
  ref: forwardedRef,
  cellId,
  children,
  className,
  columnIndex,
  diffLine,
  diffAnchor,
  handleDiffCellMouseDown,
  handleExitDialogMode,
  handleHideMarkersFromFocus,
  handleDiffSideCellSelectionBlocking,
  handleStartConversation,
  handleUserClosedMarkersDialog,
  isLeftSide,
  isRowSelected = false,
  isSplit,
  lineAnchor,
  commentDialogOpen,
  enterDialogMode,
  hasThreads,
  role,
  ...props
}: CellProps) {
  const line = diffLine as DiffLineWithSelectionInfo
  const cellRef = useRef<HTMLTableCellElement>(null)
  const getSelectedLineRange = useGetSelectedLineRange()
  const {sendAnalyticsEvent} = useAnalytics()

  // delegate the internal ref to the forwarded one if there is one
  useImperativeHandle<HTMLTableCellElement | null, HTMLTableCellElement | null>(forwardedRef, () => cellRef.current, [])

  const {disableDialogMode} = useDialogModeActions()

  const copyAnchorLink = useCallback(() => {
    const lineRange = getSelectedLineRange()
    const link = anchorLinkForSelection({line, range: lineRange, fileAnchor: diffAnchor})
    if (link) copyText(link)
  }, [diffAnchor, getSelectedLineRange, line])

  const getfirstLineSelectionOnDrag = useGetFirstLineSelectionOnDrag()
  const {clearFirstLineSelectionOnDrag} = useDiffLinesActions()
  const handleMouseUp = useCallback(
    (event: React.MouseEvent) => {
      getGlobalINPObserver()?.registerCallback({
        event: event.nativeEvent,
        cb: interaction => {
          sendAnalyticsEvent('diff-lines.inp_interaction', 'CELL_MULTI_SELECT', {
            latency: interaction.latency,
          })
        },
      })

      const firstLineSelectionOnDrag = getfirstLineSelectionOnDrag()
      if (firstLineSelectionOnDrag?.shouldStartConversation) {
        handleStartConversation?.()
      }
      clearFirstLineSelectionOnDrag()
    },
    [clearFirstLineSelectionOnDrag, getfirstLineSelectionOnDrag, handleStartConversation, sendAnalyticsEvent],
  )

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      // This will prevent unnecessary text highlighting when users are holding down shift key to select multiple lines with the mouse cursor.
      if (event.shiftKey) {
        event.preventDefault()
      }
      handleDiffCellMouseDown?.(event)
      handleDiffSideCellSelectionBlocking?.(event)
    },
    [handleDiffCellMouseDown, handleDiffSideCellSelectionBlocking],
  )

  const exitDialogMode = useCallback(() => {
    cellRef.current?.focus()
    disableDialogMode()
    handleExitDialogMode?.()
    handleUserClosedMarkersDialog?.()
  }, [disableDialogMode, handleExitDialogMode, handleUserClosedMarkersDialog])

  const getSelectedDiffLines = useGetSelectedDiffLines()
  const getDiffLinesInfo = useGetDiffLinesInfo()

  const handleOnKeyPress = useCallback(
    async (e: React.KeyboardEvent<HTMLTableCellElement>) => {
      // Handle copy code for `mod + c`
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.target === cellRef.current && (e?.metaKey || e?.ctrlKey) && e.key === 'c') {
        e.preventDefault() // Prevent default browser copy from overriding our custom copy.
        const diffLines = getDiffLinesInfo()
        await copyCode({
          diffLine,
          diffLines,
          getSelectedDiffLines,
          getSelectedLineRange,
          isSplit,
          isLeftSide,
        })
        return
      }

      // Handle copy anchor link for `mod + alt + y`
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.target === cellRef.current && (e?.metaKey || e?.ctrlKey) && e.altKey && e.key === 'y') {
        copyAnchorLink()
        return
      }

      // Handle escape press on the row
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (cellRef.current?.contains(e.target as Node) && e?.key === 'Escape') {
        exitDialogMode()
        return
      }

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.target === cellRef.current && e.key === 'Enter') {
        enterDialogMode?.()

        if (hasThreads === false) {
          handleStartConversation?.()
        }
        e.preventDefault()
      }
    },
    [
      getDiffLinesInfo,
      diffLine,
      getSelectedDiffLines,
      getSelectedLineRange,
      isSplit,
      isLeftSide,
      copyAnchorLink,
      exitDialogMode,
      enterDialogMode,
      hasThreads,
      handleStartConversation,
    ],
  )

  const key = `Cell-left:${line.left}-right:${line.right}-leftSide:${isLeftSide}`

  return (
    <td
      key={key}
      ref={cellRef}
      data-grid-cell-id={cellId}
      data-line-anchor={lineAnchor}
      data-selected={isRowSelected ? true : false}
      role={role ?? 'gridcell'}
      style={{userSelect: 'none', position: 'relative'}}
      tabIndex={-1}
      valign="top"
      className={
        className ? `focusable-grid-cell ${className} ${columnIndex < 3 ? 'left-side' : ''}` : 'focusable-grid-cell'
      }
      onKeyDown={handleOnKeyPress}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      {...props}
    >
      {children}
    </td>
  )
}
